import { useEffect, useMemo, useState } from "react";

import { RHFDatePickerInput } from "@impero-com/common/components/form/rhfFields/rhfDatePickerInput";
import { RHFStringInput } from "@impero-com/common/components/form/rhfFields/rhfStringInput";
import { FormSection } from "@impero-com/common/components/formSection/formSection";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    FieldError,
    FormField,
    FormLabel,
    useBelowBreakpoint,
} from "@impero-com/ui";
import { UseQueryResult } from "@tanstack/react-query";
import { Checkbox, DatePicker, Typography } from "antd";
import { cx } from "class-variance-authority";
import moment from "moment-timezone";
import {
    Control,
    FieldPathByValue,
    UseFormSetValue,
    UseFromSubscribe,
    UseFormTrigger,
    useController,
    useWatch,
} from "react-hook-form";

import { ControlWithoutFieldsGraphItemDtoListDto as ControlWithoutFieldsListGraphDto } from "api/generated/model";
import { TimeAndTimezone } from "components/form/rhfComponents/timeAndTimezone";
import { RHFAllocatedTimeInput } from "components/form/rhfFields/rhfAllocatedTimeInput";
import { RHFEditableUserTagListInput } from "components/form/rhfFields/rhfEditableUserTagListPlus";
import { RHFMarkdownInput } from "components/form/rhfFields/rhfMarkdownInput";
import { RHFNumberInput } from "components/form/rhfFields/rhfNumberInput";
import { RHFSelectMultipleInput } from "components/form/rhfFields/rhfSelectMultipleInput";
import { RHFUserTypeaheadInput } from "components/form/rhfFields/rhfUserTypeaheadInput";
import { InputSkeleton } from "components/form/skeleton/inputSkeleton";
import {
    ControlIdAndName,
    ControlTypeahead,
} from "org/control/components/controlTypeAhead/controlTypeahead";
import { useFetchControlQuery } from "org/control/queries/useControlQuery";
import { useControlsQuery } from "org/control/queries/useControlsQuery";
import { useTagsSuspenseQuery } from "org/tags/queries/useTagsQuery";
import { extractNestedError } from "utils/form/extractNestedError";
import { Guid } from "utils/guid";

import { useAvailableActions } from "../../../../utils/hooks/useAvailableActions";
import { useFeatureFlag } from "../../../../utils/hooks/useFeatureFlag";
import { useUserGroupsQuery } from "../../../user/queries/useUserGroupsQuery";

import { BulkControlsTable } from "./components/BulkControlsTable";
import { ControlToTestSection } from "./components/ControlToTestSection";
import { EstimatedActivitiesSection } from "./components/EstimatedActivitiesSection";
import { TestingControlTimeline } from "./components/TestingControlTimeline";
import {
    TESTING_CONTROL_ASSIGN_ANCHOR,
    TESTING_CONTROL_BULK_ANCHOR,
    TESTING_CONTROL_DETAILS_ANCHOR,
    TESTING_CONTROL_PLANNING_ANCHOR,
    TESTING_CONTROL_REMINDERS_ANCHOR,
    TESTING_CONTROL_SCHEDULE_ANCHOR,
    TESTING_CONTROL_SCOPE_ANCHOR,
    useTestingControlViewAnchorLabels,
} from "./consts";
import * as theme from "./TestingControlForm.css";
import {
    descriptionSchema,
    getDueDateSchema,
    notificationTimeSchema,
    getTitleSchema,
    TestingControlFormSchema,
    randomSampleCountSchema,
} from "./testingControlSchema";
import { TestingControlPageType } from "./types";
import {
    extractControlTagIdsForSingle,
    filterActiveTags,
    generateRemindersOptions,
    getAllTagsFromControl,
    sortRemindersDescending,
} from "./utils";

const { Text } = Typography;

interface TestingControlFormProps {
    disabled: boolean;
    control: Control<TestingControlFormSchema>;
    trigger: UseFormTrigger<TestingControlFormSchema>;
    setValue: UseFormSetValue<TestingControlFormSchema>;
    subscribe: UseFromSubscribe<TestingControlFormSchema>;
    pageType: TestingControlPageType;
}
export function TestingControlForm({
    disabled,
    control,
    trigger,
    setValue,
    subscribe,
    pageType,
}: TestingControlFormProps) {
    const { t } = useTranslation();
    const {
        userTag: { create: canCreateTag },
    } = useAvailableActions();
    const belowXl = useBelowBreakpoint("xl");

    const showControlTestingExperimentalFeature = useFeatureFlag(
        "controlTestingExperimental"
    );

    const secondReviewerUserId = useWatch({
        control,
        name: "reviewers.secondReviewer.userId",
    });
    const selectedControlIds = useWatch({
        control,
        name: "selectedControlIds",
        defaultValue: [],
    });
    const firstReviewerRemoveDisabled =
        disabled ||
        (secondReviewerUserId !== null && secondReviewerUserId !== undefined);

    const controlsQueryResult = useControlsQuery();
    const controls = controlsQueryResult.data?.content ?? [];
    const { data: userGroupTagData } = useUserGroupsQuery();

    const tagIds = useWatch({
        control,
        name: "samplingTagIds",
    });

    const filteredControlIds = controls
        .filter((control) =>
            getAllTagsFromControl(
                control.resource,
                userGroupTagData ?? []
            ).some((tagId) => tagIds.includes(tagId))
        )
        .map((control) => control.resource.id);

    //Check selectedControlIds against the filtered control IDs
    const filteredControlsByTag = selectedControlIds.filter((controlId) =>
        filteredControlIds.includes(controlId)
    );

    // Updates selectedControlIds when filteredControlsByTag changes in bulk create mode
    useEffect(() => {
        switch (pageType) {
            case "BULK_CREATE":
                // Only update if there's a mismatch between current selectedControlIds and filteredControlsByTag
                const hasMismatch =
                    selectedControlIds.length !==
                        filteredControlsByTag.length ||
                    selectedControlIds.some(
                        (id, index) => id !== filteredControlsByTag[index]
                    );

                if (hasMismatch) {
                    setValue("selectedControlIds", filteredControlsByTag);
                }
                break;
            case "CREATE":
            case "EDIT":
                break;
            default:
                assertNever(pageType);
        }
    }, [pageType, selectedControlIds, filteredControlsByTag, setValue]);

    const fetchControlQuery = useFetchControlQuery();
    const { data: tagsData } = useTagsSuspenseQuery();

    // Updates tagIds when controlId or samplingTagIds changes
    useEffect(() => {
        return subscribe({
            name: ["controlId", "samplingTagIds"],
            exact: true,
            formState: {
                values: true,
            },
            callback: ({ values, isDirty, name }) => {
                const updateTitleAndTags = async (
                    controlId: string,
                    samplingTagIds: Set<Guid>
                ) => {
                    const {
                        data: {
                            content: { resource },
                            linkedResources: { userGroups },
                        },
                    } = await fetchControlQuery(controlId);
                    // Update title only on control change
                    if (name === "controlId") {
                        setValue(
                            "title",
                            t("TestingControlView_Title_Autofill", {
                                title: resource.title,
                            })
                        );
                    }

                    const userGroupUserTags = Object.fromEntries(
                        userGroups.map(({ resource }) => [
                            resource.id,
                            new Set(
                                resource.userGroupUsers.flatMap(
                                    (user) => user.userTagIds
                                )
                            ),
                        ])
                    );
                    const selectedTagIds = extractControlTagIdsForSingle(
                        resource,
                        userGroupUserTags,
                        samplingTagIds
                    );

                    setValue(
                        "tagIds",
                        filterActiveTags(selectedTagIds, tagsData)
                    );
                };

                // Check dirtiness to avoid modification on load
                // Also check if a controlId is defined.
                // Missing control id might mean we are on the bulk creation page.
                if (isDirty && "controlId" in values && values.controlId) {
                    void updateTitleAndTags(
                        values.controlId,
                        new Set(values.samplingTagIds)
                    );
                }
            },
        });
    }, [subscribe, fetchControlQuery, setValue, t, tagsData]);

    const isSingleMode = () => {
        switch (pageType) {
            case "BULK_CREATE":
                return false;
            case "CREATE":
            case "EDIT":
            default:
                return true;
        }
    };
    const anchorLabels = useTestingControlViewAnchorLabels(isSingleMode());

    const scopeSectionContent = (
        <>
            <div className={theme.formSection}>
                <RHFEditableUserTagListInput
                    cascaderClassName={theme.tagCascader}
                    control={control}
                    name={"samplingTagIds"}
                    canCreateTag={canCreateTag}
                    disabled={disabled}
                    label={
                        isSingleMode()
                            ? t("TestingControlView_EntityTags_Label")
                            : t("TestingControlView_EntityTags_Label_Bulk")
                    }
                    trigger={trigger}
                />
            </div>
            <Period control={control} disabled={disabled} trigger={trigger} />

            <RandomSampling
                control={control}
                disabled={disabled}
                trigger={trigger}
            />
        </>
    );

    const scopeSection = (
        <FormSection
            id={TESTING_CONTROL_SCOPE_ANCHOR}
            titleLabel={anchorLabels[TESTING_CONTROL_SCOPE_ANCHOR]}
            withDivider
        >
            {isSingleMode() ? (
                <div
                    className={cx(
                        theme.detailsContents,
                        belowXl && theme.detailsContentsBelowXl
                    )}
                >
                    <div>{scopeSectionContent}</div>
                    <EstimatedActivitiesSection
                        disableInTransition={pageType !== "CREATE"}
                        control={control}
                    />
                </div>
            ) : (
                scopeSectionContent
            )}
        </FormSection>
    );

    const detailsSection = (
        <FormSection
            id={TESTING_CONTROL_DETAILS_ANCHOR}
            titleLabel={anchorLabels[TESTING_CONTROL_DETAILS_ANCHOR]}
            withDivider
        >
            <div
                className={cx(
                    theme.detailsContents,
                    belowXl && theme.detailsContentsBelowXl
                )}
            >
                <div className={theme.formSection}>
                    <TestingControlControlTypeahead
                        control={control}
                        disabled={disabled || pageType === "EDIT"}
                        controlQueryResult={controlsQueryResult}
                    />
                    <RHFEditableUserTagListInput
                        cascaderClassName={theme.tagCascader}
                        control={control}
                        name={"tagIds"}
                        canCreateTag={canCreateTag}
                        disabled={disabled}
                        label={t("TestingControlView_Tags_Label")}
                    />
                    <RHFStringInput
                        control={control}
                        schema={getTitleSchema(t)}
                        name={"title"}
                        disabled={disabled}
                        label={t("TestingControlView_Title_Label")}
                        placeholder={t("TestingControlView_Title_Placeholder")}
                    />
                    <RHFMarkdownInput
                        control={control}
                        schema={descriptionSchema}
                        name="description"
                        disabled={disabled}
                        label={t("TestingControlView_Description_Label")}
                        placeholder={t(
                            "TestingControlView_Description_Placeholder"
                        )}
                    />
                </div>
                <ControlToTestSection
                    disableInTransition={pageType !== "CREATE"}
                    control={control}
                />
            </div>
        </FormSection>
    );

    const bulkSection = (
        <FormSection
            id={TESTING_CONTROL_BULK_ANCHOR}
            titleLabel={anchorLabels[TESTING_CONTROL_BULK_ANCHOR]}
            withDivider
        >
            <div className={theme.sectionContent}>
                <Text>{t("TestingControlView_Bulk_DescText")}</Text>
                <BulkControlsTable control={control} />
            </div>
        </FormSection>
    );

    const assignSection = (
        <FormSection
            id={TESTING_CONTROL_ASSIGN_ANCHOR}
            titleLabel={anchorLabels[TESTING_CONTROL_ASSIGN_ANCHOR]}
            withDivider
        >
            <div
                className={cx(
                    theme.assignees,
                    belowXl && theme.assigneesNarrow
                )}
            >
                <RHFUserTypeaheadInput
                    control={control}
                    name="responsible.userId"
                    disabled={disabled}
                    label={t("TestingControlView_Responsible_Label")}
                    placeholder={t(
                        "TestingControlView_Responsible_Placeholder"
                    )}
                />

                <RHFUserTypeaheadInput
                    control={control}
                    name="reviewers.reviewer.userId"
                    disabled={disabled}
                    label={t("TestingControlView_Reviewer_Label")}
                    placeholder={t("TestingControlView_Reviewer_Placeholder")}
                    onRemove={() => {
                        setValue("reviewers.reviewer.userId", null, {
                            shouldDirty: true,
                        });
                        setValue("reviewers.reviewer", undefined);
                    }}
                    removeDisabled={firstReviewerRemoveDisabled}
                />

                <RHFUserTypeaheadInput
                    control={control}
                    name="reviewers.secondReviewer.userId"
                    disabled={disabled}
                    label={t("TestingControlView_SecondReviewer_Label")}
                    placeholder={t(
                        "TestingControlView_SecondReviewer_Placeholder"
                    )}
                    onRemove={() => {
                        setValue("reviewers.secondReviewer.userId", null, {
                            shouldDirty: true,
                        });
                        setValue("reviewers.secondReviewer", undefined);
                    }}
                />
            </div>
        </FormSection>
    );

    const schedulePlanningSection = (
        <div className={theme.scheduleSectionContent}>
            <div className={theme.schedulePlanningSectionsContainer}>
                <FormSection
                    id={TESTING_CONTROL_SCHEDULE_ANCHOR}
                    titleLabel={anchorLabels[TESTING_CONTROL_SCHEDULE_ANCHOR]}
                >
                    <div
                        className={cx(
                            theme.inputRow,
                            belowXl && theme.inputRowNarrow
                        )}
                    >
                        <RHFDatePickerInput
                            control={control}
                            name="dueDate"
                            schema={getDueDateSchema(t)}
                            disabled={disabled}
                            label={t("TestingControlView_DueDate_Label")}
                        />
                        <TimeAndTimezone
                            control={control}
                            timezoneInputName="timezoneName"
                            timeInputName="dueDate"
                            timeInputSchema={notificationTimeSchema}
                            disabled={disabled}
                            timeInputLabel={t(
                                "TestingControlView_DueTime_Label"
                            )}
                        />
                    </div>
                </FormSection>

                <FormSection
                    id={TESTING_CONTROL_PLANNING_ANCHOR}
                    titleLabel={anchorLabels[TESTING_CONTROL_PLANNING_ANCHOR]}
                    tooltip={t("TestingControlView_Planning_Tooltip")}
                    withDivider
                >
                    <div
                        className={cx(
                            theme.inputRow,
                            belowXl && theme.inputRowNarrow
                        )}
                    >
                        <RHFAllocatedTimeInput
                            control={control}
                            name="responsible.allocatedTime"
                            disabled={disabled}
                            label={t(
                                "TestingControlView_AllocatedTime_ResponsibleLabel"
                            )}
                            nodeAfter={t(
                                "TestingControlView_AllocatedTime_ResponsibleLabelAfter"
                            )}
                            disableChangeUnit
                        />
                        <TimeAndTimezone
                            control={control}
                            timezoneInputName="timezoneName"
                            timeInputName="notificationTime"
                            timeInputSchema={notificationTimeSchema}
                            disabled={disabled}
                            timeInputLabel={t(
                                "TestingControlView_ResponsibleNotificationTime_Label"
                            )}
                            readOnlyTimezone
                        />
                    </div>
                    <div
                        className={cx(
                            theme.inputRow,
                            belowXl && theme.inputRowNarrow
                        )}
                    >
                        <ConditionalAllocatedTime
                            control={control}
                            disabled={disabled}
                            assigneePath="reviewers.reviewer"
                            label={t(
                                "TestingControlView_AllocatedTime_ReviewerLabel"
                            )}
                            nodeAfter={t(
                                "TestingControlView_AllocatedTime_ReviewerLabelAfter"
                            )}
                        />
                        <ConditionalAllocatedTime
                            control={control}
                            assigneePath="reviewers.secondReviewer"
                            disabled={disabled}
                            label={t(
                                "TestingControlView_AllocatedTime_SecondReviewerLabel"
                            )}
                            nodeAfter={t(
                                "TestingControlView_AllocatedTime_SecondReviewerLabelAfter"
                            )}
                        />
                    </div>
                </FormSection>
            </div>
            <FormSection
                styleVariant="secondary"
                titleLabel={t("TestingControlView_SchedulePreviewLabel")}
                className={theme.timelineSection}
                stickyContent
                withDivider
            >
                <TestingControlTimeline control={control} />
            </FormSection>
        </div>
    );

    const remindersOptions = useMemo(() => generateRemindersOptions(t), [t]);

    const remindersSection = showControlTestingExperimentalFeature ? (
        <FormSection
            id={TESTING_CONTROL_REMINDERS_ANCHOR}
            tooltip={t("TestingControlView_RemindersTooltip")}
            titleLabel={anchorLabels[TESTING_CONTROL_REMINDERS_ANCHOR]}
        >
            <RHFSelectMultipleInput
                label={t("TestingControlView_RemindersBeforeDueDate")}
                name="remindersBefore"
                control={control}
                placeholder={t("TestingControlView_RemindersPlaceholder")}
                options={remindersOptions}
                nodeAfter={t(
                    "TestingControlView_RemindersBeforeDueDateTextBefore"
                )}
                disabled={disabled}
                sortValues={sortRemindersDescending}
                showSearch
            />

            <RHFSelectMultipleInput
                label={t("TestingControlView_RemindersAfterDueDate")}
                name="remindersAfter"
                control={control}
                placeholder={t("TestingControlView_RemindersPlaceholder")}
                options={remindersOptions}
                nodeAfter={t(
                    "TestingControlView_RemindersAfterDueDateTextAfter"
                )}
                disabled={disabled}
                sortValues={sortRemindersDescending}
                showSearch
            />
        </FormSection>
    ) : null;

    switch (pageType) {
        case "BULK_CREATE":
            return (
                <div>
                    {scopeSection}
                    {schedulePlanningSection}
                    {bulkSection}
                    {assignSection}
                    {remindersSection}
                </div>
            );
        case "EDIT":
        case "CREATE":
            return (
                <div>
                    {detailsSection}
                    {schedulePlanningSection}
                    {scopeSection}
                    {assignSection}
                    {remindersSection}
                </div>
            );
        default:
            return assertNever(pageType);
    }
}

interface TestingControlControlTypeaheadProps {
    control: Control<TestingControlFormSchema>;
    disabled: boolean;
    controlQueryResult: UseQueryResult<ControlWithoutFieldsListGraphDto>;
}
function TestingControlControlTypeahead({
    control,
    disabled,
    controlQueryResult,
}: TestingControlControlTypeaheadProps) {
    const { t } = useTranslation();
    const { field, fieldState } = useController({
        name: "controlId",
        control,
        disabled,
    });
    const {
        data: controlsData,
        error: controlsQueryError,
        isLoading,
        isSuccess: isControlsQuerySuccess,
    } = controlQueryResult;

    const label = t("TestingControlView_Control_Label");
    if (!isControlsQuerySuccess) {
        return (
            <InputSkeleton
                label={label}
                error={controlsQueryError?.message}
                enableAnimation={isLoading}
            />
        );
    }

    const getInjectedControls = (controlId: string | undefined) => {
        if (controlId === undefined) {
            return undefined; // No control selected
        }

        const controlIdInResource = controlsData.content.some(
            (control) => control.resource.id === field.value
        );
        if (controlIdInResource) {
            return undefined; // User has access to control - no need to inject
        }
        const placeholderControl: ControlIdAndName = {
            id: field.value ?? "noControl",
            name: t("TestingControlView_NoControlViewAccess"),
        };
        return [placeholderControl];
    };

    return (
        <ControlTypeahead
            error={extractNestedError(fieldState.error)}
            disabled={Boolean(field.disabled)}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            fieldRef={field.ref}
            injectedControls={getInjectedControls(field.value)}
            label={t("TestingControlView_Control_Label")}
        />
    );
}

interface ConditionalAllocatedTimeProps {
    control: Control<TestingControlFormSchema>;
    disabled: boolean;
    assigneePath: FieldPathByValue<
        TestingControlFormSchema,
        TestingControlFormSchema["reviewers"]["reviewer"]
    >;
    label: string;
    nodeAfter: string;
}
function ConditionalAllocatedTime({
    control,
    disabled,
    assigneePath,
    label,
    nodeAfter,
}: ConditionalAllocatedTimeProps) {
    const reviewer = useWatch({ control, name: `${assigneePath}.userId` });
    const hasReviewer = !!reviewer;
    if (!hasReviewer) {
        return null;
    }
    return (
        <RHFAllocatedTimeInput
            control={control}
            name={`${assigneePath}.allocatedTime`}
            disabled={disabled}
            label={label}
            nodeAfter={nodeAfter}
        />
    );
}

interface PeriodProps {
    control: Control<TestingControlFormSchema>;
    disabled: boolean;
    trigger: UseFormTrigger<TestingControlFormSchema>;
}
function Period({ control, disabled, trigger }: PeriodProps) {
    const { t } = useTranslation();

    const { fieldState } = useController({ control, name: "period" });

    const handleBlur = () => {
        trigger("period");
    };

    return (
        <div>
            <FormLabel helpText={t("TestingControlView_Period_HelpText")}>
                {t("TestingControlView_Period_Label")}
            </FormLabel>
            <div className={theme.period}>
                <PeriodMonthYearPicker
                    control={control}
                    name="period.start"
                    disabled={disabled}
                    onBlur={handleBlur}
                    placeholder={t(
                        "TestingControlView_Period_Start_Placeholder"
                    )}
                />
                <Text>{t("TestingControlView_Period_To")}</Text>
                <PeriodMonthYearPicker
                    control={control}
                    name="period.end"
                    disabled={disabled}
                    onBlur={handleBlur}
                    placeholder={t("TestingControlView_Period_End_Placeholder")}
                />
            </div>

            <FieldError error={fieldState.error?.message} />
        </div>
    );
}

interface PeriodMonthYearPickerProps {
    control: Control<TestingControlFormSchema>;
    disabled: boolean;
    name: "period.start" | "period.end";
    onBlur: () => void;
    placeholder: string;
}
function PeriodMonthYearPicker({
    control,
    disabled,
    name,
    onBlur,
    placeholder,
}: PeriodMonthYearPickerProps) {
    const { field, fieldState } = useController({ control, name, disabled });

    return (
        <FormField error={fieldState.error?.message} lite>
            <DatePicker
                // eslint-disable-next-line react-hooks/refs
                ref={field.ref}
                picker="month"
                format="MMMM YYYY"
                // eslint-disable-next-line react-hooks/refs
                value={field.value ? moment(field.value) : null}
                onChange={(date) => field.onChange(date ? date.toDate() : null)}
                onBlur={onBlur}
                disabled={disabled}
                placeholder={placeholder}
                status={fieldState.error ? "error" : ""}
                className={theme.periodMonthYear}
            />
        </FormField>
    );
}

interface RandomSamplingProps {
    control: Control<TestingControlFormSchema>;
    disabled: boolean;
    trigger: UseFormTrigger<TestingControlFormSchema>;
}
function RandomSampling({ control, disabled }: RandomSamplingProps) {
    const { t } = useTranslation();
    const { field: randomSampleCountField } = useController({
        control,
        name: "randomSampleCount",
    });

    const [isChecked, setIsChecked] = useState(
        randomSampleCountField.value !== undefined &&
            randomSampleCountField.value > 0
    );

    const handleCheckboxChange = (checked: boolean) => {
        setIsChecked(checked);
        if (!checked) {
            randomSampleCountField.onChange(0);
        }
    };

    return (
        <div className={theme.sectionContent}>
            <div>
                <Checkbox
                    checked={isChecked}
                    disabled={disabled}
                    onChange={(e) => handleCheckboxChange(e.target.checked)}
                >
                    {t("TestingActivities_Random_Sampling_Enable")}
                </Checkbox>
            </div>
            {isChecked && (
                <RHFNumberInput
                    control={control}
                    schema={randomSampleCountSchema}
                    name="randomSampleCount"
                    disabled={disabled}
                    label={t("TestingActivities_Random_Sampling_Size")}
                />
            )}
        </div>
    );
}
